import { useQuery } from '@tanstack/react-query';
import { fetchApprovedMLSList as fetchApprovedMLSListApi } from '../../../api/mls';

/**
 * Interface for an Approved MLS entry
 */
export interface ApprovedMLS {
  boardCode: string; // Document ID
  mlsName: string; // MLS Name
  isApproved: boolean;
}

/**
 * Query keys for approved MLS data
 */
export const approvedMLSKeys = {
  all: ['approvedMLS'] as const,
  list: () => [...approvedMLSKeys.all, 'list'] as const,
};

/**
 * Fetch all MLS entries from Firestore
 * Shows ALL MLS regardless of approval status
 * Sorts by isApproved in JavaScript (approved first) to avoid Firestore orderBy excluding documents
 */
const fetchApprovedMLSList = async (): Promise<ApprovedMLS[]> => {
  // Source swapped Firestore MLSLists -> Spanner via the mls service; shape unchanged.
  const mlsList = await fetchApprovedMLSListApi();

  // Sort in JavaScript: approved MLS first, then alphabetically by boardCode
  return [...mlsList].sort((a, b) => {
    if (a.isApproved !== b.isApproved) {
      return a.isApproved ? -1 : 1;
    }
    return a.boardCode.localeCompare(b.boardCode);
  });
};

/**
 * Custom hook to fetch all approved MLS entries
 * @param enabled Whether the query should be enabled
 * @returns Query result with approved MLS list
 */
export const useApprovedMLSList = (enabled = true) => {
  return useQuery({
    queryKey: approvedMLSKeys.list(),
    queryFn: fetchApprovedMLSList,
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};
